import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Notification from "../../components/Notification.js";
import { API_URL } from "../../config.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatTimestamp, formatToDayMonthYear, formatCurrency } from "../../utils/TextUtils.js";

function Expenses() {
    const [expenses, setExpenses] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterDateFrom, setFilterDateFrom] = useState("");
    const [filterDateTo, setFilterDateTo] = useState("");
    const [filterMonth, setFilterMonth] = useState("");
    const [filterRoute, setFilterRoute] = useState("");
    const [filterMinAmount, setFilterMinAmount] = useState("");
    const [filterMaxAmount, setFilterMaxAmount] = useState("");
    const [filterRouteSpecific, setFilterRouteSpecific] = useState("");
    const [showDeleted, setShowDeleted] = useState(false);
    const [deletionReason, setDeletionReason] = useState("");
    const [routes, setRoutes] = useState([]);
    const [perPage, setPerPage] = useState(10);
    const [sortBy, setSortBy] = useState("newest");
    const [validationErrors, setValidationErrors] = useState({});
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");

    // Page size options
    const pageSizeOptions = [10, 20, 50, 100];

    // Sort options
    const sortOptions = [
        { value: "newest", label: "Newest First" },
        { value: "oldest", label: "Oldest First" },
        { value: "amount_asc", label: "Amount: Low to High" },
        { value: "amount_desc", label: "Amount: High to Low" }
    ];

    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin', 'collection_center_manager']));
    };

    const navigate = useNavigate();

    // Route specific filter options
    const routeSpecificOptions = [
        { value: "", label: "All Expenses" },
        { value: "1", label: "Route Specific" },
        { value: "0", label: "General Expenses" }
    ];

    // Fetch routes from API
    const fetchRoutes = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/collection-routes`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setRoutes(data || []);
            }
        } catch (error) {
            console.error("Error fetching routes:", error);
        }
    }, [token]);

    // Validate amount filters
    const validateAmountFilters = () => {
        const errors = {};
        
        // If both min and max are provided, validate they make sense
        if (filterMinAmount && filterMaxAmount) {
            const min = parseFloat(filterMinAmount);
            const max = parseFloat(filterMaxAmount);
            if (min > max) {
                errors.max_amount = ["Maximum amount must be greater than or equal to minimum amount."];
            }
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Fetch expenses
    const fetchExpenses = useCallback(async () => {
        // Validate filters before fetching
        if (!validateAmountFilters()) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError("");
            setValidationErrors({});
            
            let url = `${API_URL}/expenses?page=${currentPage}&limit=${perPage}`;
            const params = new URLSearchParams();
            
            if (searchTerm) params.append('search', searchTerm);
            if (filterDateFrom) params.append('from_date', filterDateFrom);
            if (filterDateTo) params.append('to_date', filterDateTo);
            if (filterMonth) params.append('month', filterMonth);
            if (filterRoute) params.append('route_id', filterRoute);
            if (filterMinAmount) params.append('min_amount', filterMinAmount);
            if (filterMaxAmount) params.append('max_amount', filterMaxAmount);
            if (filterRouteSpecific !== "") params.append('is_route_specific', filterRouteSpecific);
            if (showDeleted) params.append('show_deleted', '1');
            
            // Sorting
            if (sortBy) {
                switch(sortBy) {
                    case "newest":
                        params.append('sort_by', 'created_at');
                        params.append('sort_order', 'desc');
                        break;
                    case "oldest":
                        params.append('sort_by', 'created_at');
                        params.append('sort_order', 'asc');
                        break;
                    case "amount_asc":
                        params.append('sort_by', 'amount');
                        params.append('sort_order', 'asc');
                        break;
                    case "amount_desc":
                        params.append('sort_by', 'amount');
                        params.append('sort_order', 'desc');
                        break;
                    default:
                        break;
                }
            }

            const response = await fetch(`${url}&${params.toString()}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });

            if (response.ok) {
                const data = await response.json();
                setExpenses(data.data.data || []);
                setTotalPages(data.data.last_page || 1);
                setTotalAmount(data.total_amount || 0);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                // Check if it's a validation error
                if (errorData.errors) {
                    setValidationErrors(errorData.errors);
                    // Display validation errors as notification
                    const errorMessages = Object.values(errorData.errors).flat();
                    setNotification({ 
                        type: "danger", 
                        message: errorMessages.join(", ") 
                    });
                } else {
                    throw new Error(errorData.message || "Failed to fetch expenses");
                }
            }
        } catch (error) {
            setError(error.message || "An error occurred while fetching expenses.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, searchTerm, filterDateFrom, filterDateTo, filterMonth, filterRoute, 
        filterMinAmount, filterMaxAmount, filterRouteSpecific, showDeleted, sortBy, navigate, token]);

    // Fetch routes on component mount
    useEffect(() => {
        fetchRoutes();
    }, [fetchRoutes]);

    // Fetch expenses when dependencies change
    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null);
        } else {
            setIsDropdownOpen(index);
        }
    };

    const handleDeleteClick = (expense) => {
        setSelectedExpense(expense);
        setDeletionReason("");
        setIsConfirmDialogOpen(true);
    };

    const confirmDeleteExpense = async () => {
        if (selectedExpense) {
            try {
                const response = await fetch(`${API_URL}/expenses/${selectedExpense.id}`, {
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                    body: JSON.stringify({ deletion_reason: deletionReason }),
                });

                if (response.ok) {
                    setNotification({ type: "success", message: 'Expense deleted successfully' });
                    fetchExpenses();
                } else if (response.status === 401) {
                    navigate("/logout");
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: 'Expense not found.' });
                } else {
                    const errorData = await response.json();
                    setNotification({ 
                        type: "danger", 
                        message: errorData.message || 'There was an error deleting the expense.' 
                    });
                }
            } catch (error) {
                setNotification({ 
                    message: 'An error occurred while deleting the expense.', 
                    type: 'danger' 
                });
                console.error("Error:", error);
            } finally {
                setIsConfirmDialogOpen(false);
                setSelectedExpense(null);
                setDeletionReason("");
            }
        }
    };

    const handleRestoreClick = (expense) => {
        setSelectedExpense(expense);
        setIsRestoreDialogOpen(true);
    };

    const confirmRestoreExpense = async () => {
        if (selectedExpense) {
            try {
                const response = await fetch(`${API_URL}/expenses/${selectedExpense.id}/restore`, {
                    method: 'POST',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    setNotification({ type: "success", message: 'Expense restored successfully' });
                    fetchExpenses();
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    const errorData = await response.json();
                    setNotification({ 
                        type: "danger", 
                        message: errorData.message || 'There was an error restoring the expense.' 
                    });
                }
            } catch (error) {
                setNotification({ 
                    message: 'An error occurred while restoring the expense.', 
                    type: 'danger' 
                });
                console.error("Error:", error);
            } finally {
                setIsRestoreDialogOpen(false);
                setSelectedExpense(null);
            }
        }
    };

    const cancelDeleteExpense = () => {
        setIsConfirmDialogOpen(false);
        setSelectedExpense(null);
        setDeletionReason("");
    };

    const cancelRestoreExpense = () => {
        setIsRestoreDialogOpen(false);
        setSelectedExpense(null);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleFilterDateFromChange = (event) => {
        setFilterDateFrom(event.target.value);
        setCurrentPage(1);
        // Clear validation errors when changing filters
        setValidationErrors({});
    };

    const handleFilterDateToChange = (event) => {
        setFilterDateTo(event.target.value);
        setCurrentPage(1);
        setValidationErrors({});
    };

    const handleFilterMonthChange = (event) => {
        setFilterMonth(event.target.value);
        setCurrentPage(1);
        setValidationErrors({});
    };

    const handleFilterRouteChange = (event) => {
        setFilterRoute(event.target.value);
        setCurrentPage(1);
        setValidationErrors({});
    };

    const handleFilterMinAmountChange = (event) => {
        setFilterMinAmount(event.target.value);
        setCurrentPage(1);
        setValidationErrors({});
    };

    const handleFilterMaxAmountChange = (event) => {
        setFilterMaxAmount(event.target.value);
        setCurrentPage(1);
        setValidationErrors({});
    };

    const handleFilterRouteSpecificChange = (event) => {
        setFilterRouteSpecific(event.target.value);
        setCurrentPage(1);
        setValidationErrors({});
    };

    const handleShowDeletedChange = (event) => {
        setShowDeleted(event.target.checked);
        setCurrentPage(1);
        setValidationErrors({});
    };

    const handleSortChange = (event) => {
        setSortBy(event.target.value);
        setCurrentPage(1);
        setValidationErrors({});
    };

    const handlePerPageChange = (event) => {
        setPerPage(parseInt(event.target.value));
        setCurrentPage(1);
        setValidationErrors({});
    };

    const clearFilters = () => {
        setSearchTerm("");
        setFilterDateFrom("");
        setFilterDateTo("");
        setFilterMonth("");
        setFilterRoute("");
        setFilterMinAmount("");
        setFilterMaxAmount("");
        setFilterRouteSpecific("");
        setShowDeleted(false);
        setSortBy("newest");
        setPerPage(10);
        setCurrentPage(1);
        setValidationErrors({});
        setError("");
    };

    const isFilterActive = searchTerm || filterDateFrom || filterDateTo || filterMonth || 
                          filterRoute || filterMinAmount || filterMaxAmount || filterRouteSpecific !== "" || 
                          showDeleted || sortBy !== "newest" || perPage !== 10;

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isDropdownOpen]);

    // Helper function to get sort icon
    const getSortIcon = () => {
        switch(sortBy) {
            case "amount_asc":
                return <i className="fas fa-sort-amount-up" style={{ color: '#00d1b2' }}></i>;
            case "amount_desc":
                return <i className="fas fa-sort-amount-down" style={{ color: '#00d1b2' }}></i>;
            case "newest":
                return <i className="fas fa-sort-numeric-down" style={{ color: '#00d1b2' }}></i>;
            case "oldest":
                return <i className="fas fa-sort-numeric-up" style={{ color: '#00d1b2' }}></i>;
            default:
                return null;
        }
    };

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <span className="title">Expenses</span>
                        </div>
                    </div>

                    <div className="level-item has-text-centered">
                        <div>
                            <p>
                                Amount: <strong>
                                    {loading ? '...' : formatCurrency(totalAmount)}
                                </strong>
                            &nbsp; | &nbsp;
                                <small>Total Items: {loading ? '...' : expenses.length}</small>
                            </p>
                        </div>
                    </div>

                    <div className="level-right">
                        <p className="level-item">
                            <Link to="add" className="button primary-bg">
                                <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; Add Expense
                            </Link>
                        </p>
                    </div>
                </nav>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {/* Validation Error Display */}
                {Object.keys(validationErrors).length > 0 && (
                    <div className="notification is-danger">
                        <button className="delete" onClick={() => setValidationErrors({})}></button>
                        <strong>Validation Errors:</strong>
                        <ul>
                            {Object.entries(validationErrors).map(([field, errors]) => (
                                <li key={field}>
                                    <strong>{field.replace('_', ' ').toUpperCase()}:</strong> {errors.join(', ')}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Filters */}
                <div className="box" style={{ marginBottom: "1rem" }}>
                    <div className="columns is-multiline is-vcentered">
                        {/* Row 1: Search, Route, Type, Sort, Per Page */}
                        <div className="column is-12">
                            <div className="columns is-multiline">
                                <div className="column is-3">
                                    <div className="field">
                                        <label className="label">Search</label>
                                        <div className="control">
                                            <input
                                                className="input"
                                                type="text"
                                                placeholder="Search by name or description"
                                                value={searchTerm}
                                                onChange={handleSearch}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">Route</label>
                                        <div className="control">
                                            <div className="select is-fullwidth">
                                                <select
                                                    value={filterRoute}
                                                    onChange={handleFilterRouteChange}
                                                >
                                                    <option value="">All Routes</option>
                                                    {routes.map((route) => (
                                                        <option key={route.id} value={route.id}>
                                                            {route.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">Expense Type</label>
                                        <div className="control">
                                            <div className="select is-fullwidth">
                                                <select
                                                    value={filterRouteSpecific}
                                                    onChange={handleFilterRouteSpecificChange}
                                                >
                                                    {routeSpecificOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">Sort By</label>
                                        <div className="control">
                                            <div className="select is-fullwidth">
                                                <select
                                                    value={sortBy}
                                                    onChange={handleSortChange}
                                                >
                                                    {sortOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">Per Page</label>
                                        <div className="control">
                                            <div className="select is-fullwidth">
                                                <select
                                                    value={perPage}
                                                    onChange={handlePerPageChange}
                                                >
                                                    {pageSizeOptions.map((size) => (
                                                        <option key={size} value={size}>
                                                            {size}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="column is-1">
                                    <div className="field">
                                        <label className="label">&nbsp;</label>
                                        <div className="control">
                                            <button
                                                className="button is-light is-fullwidth"
                                                onClick={clearFilters}
                                                disabled={!isFilterActive}
                                            >
                                                <i className="fa fa-times"></i>&nbsp; Clear
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Row 2: Date From, Date To, Month, Min Amount, Max Amount, Show Deleted */}
                        <div className="column is-12">
                            <div className="columns is-multiline">
                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">From Date</label>
                                        <div className="control">
                                            <input
                                                type="date"
                                                className="input"
                                                value={filterDateFrom}
                                                onChange={handleFilterDateFromChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">To Date</label>
                                        <div className="control">
                                            <input
                                                type="date"
                                                className="input"
                                                value={filterDateTo}
                                                onChange={handleFilterDateToChange}
                                                min={filterDateFrom}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">Month</label>
                                        <div className="control">
                                            <input
                                                type="month"
                                                className="input"
                                                value={filterMonth}
                                                onChange={handleFilterMonthChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">Min Amount</label>
                                        <div className="control">
                                            <input
                                                className={`input ${validationErrors.min_amount ? 'is-danger' : ''}`}
                                                type="number"
                                                placeholder="Min"
                                                value={filterMinAmount}
                                                onChange={handleFilterMinAmountChange}
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                        {validationErrors.min_amount && (
                                            <p className="help is-danger">{validationErrors.min_amount[0]}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">Max Amount</label>
                                        <div className="control">
                                            <input
                                                className={`input ${validationErrors.max_amount ? 'is-danger' : ''}`}
                                                type="number"
                                                placeholder="Max"
                                                value={filterMaxAmount}
                                                onChange={handleFilterMaxAmountChange}
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                        {validationErrors.max_amount && (
                                            <p className="help is-danger">{validationErrors.max_amount[0]}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="column is-2">
                                    <div className="field">
                                        <label className="label">Show Deleted</label>
                                        <div className="control" style={{ marginTop: "0.4rem" }}>
                                            <label className="checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={showDeleted}
                                                    onChange={handleShowDeletedChange}
                                                />
                                                &nbsp; Include deleted
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="has-text-centered" style={{ padding: "2rem" }}>
                        <i className="fa fa-spinner fa-spin fa-2x"></i>
                        <p>Loading expenses...</p>
                    </div>
                ) : error ? (
                    <div className="notification is-danger">
                        <button className="delete" onClick={() => setError("")}></button>
                        {error}
                    </div>
                ) : (
                    <>
                        <table className="table is-striped is-fullwidth is-hoverable">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>
                                        Amount {getSortIcon()}
                                    </th>
                                    <th>Incurred On</th>
                                    <th>Type</th>
                                    <th>Route</th>
                                    <th>Status</th>
                                    <th>Description</th>
                                    <th>Posted By</th>
                                    <th>Created At</th>
                                    <th>Options</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.length > 0 ? (
                                    expenses.map((expense, index) => (
                                        <tr key={expense.id} className={expense.is_deleted ? 'has-background-danger-light' : ''}>
                                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                                            <td>
                                                <strong>{expense.name}</strong>
                                            </td>
                                            <td className={`has-text-weight-bold`}>
                                                {formatCurrency(expense.amount)}
                                            </td>
                                            <td>
                                                {formatToDayMonthYear(expense.incurred_on)}
                                            </td>
                                            <td>
                                                {expense.is_route_specific ? (
                                                    <span className="tag is-info">Route Specific</span>
                                                ) : (
                                                    <span className="tag is-light">General</span>
                                                )}
                                            </td>
                                            <td>
                                                {expense.is_route_specific ? (
                                                    expense.collection_route?.name || 'N/A'
                                                ) : (
                                                    <span className="has-text-grey-light">All Routes</span>
                                                )}
                                            </td>
                                            <td>
                                                {expense.is_deleted ? (
                                                    <span className="tag is-danger">Deleted</span>
                                                ) : (
                                                    <span className="tag is-success">Active</span>
                                                )}
                                            </td>
                                            <td>
                                                {expense.description ? (
                                                    <span title={expense.description}>
                                                        {expense.description.length > 30 
                                                            ? expense.description.substring(0, 30) + '...' 
                                                            : expense.description}
                                                    </span>
                                                ) : (
                                                    <span className="has-text-grey-light">-</span>
                                                )}
                                            </td>
                                            <td>
                                                {expense.created_by?.name || 'N/A'}
                                            </td>
                                            <td>
                                                {formatTimestamp(expense.created_at)}
                                            </td>
                                            <td>
                                                <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                    <div className="dropdown-trigger">
                                                        <button
                                                            className="button is-white"
                                                            aria-haspopup="true"
                                                            aria-controls="dropdown-menu"
                                                            onClick={() => toggleDropdown(index)}
                                                        >
                                                            <span className="icon">
                                                                <i className="fas fa-ellipsis-v"></i>
                                                            </span>
                                                        </button>
                                                    </div>
                                                    <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                        <div className="dropdown-content">
                                                            {canEdit(userRole) && (
                                                                <>
                                                                    <Link to={`${expense.id}/details`} className="dropdown-item">
                                                                        <span className="icon">
                                                                            <i className="fas fa-info-circle"></i>
                                                                        </span>
                                                                        <span>View Details</span>
                                                                    </Link>
                                                                    {!expense.is_deleted && (
                                                                        <>
                                                                            <Link to={`${expense.id}/edit`} className="dropdown-item">
                                                                                <span className="icon">
                                                                                    <i className="fas fa-edit"></i>
                                                                                </span>
                                                                                <span>Edit</span>
                                                                            </Link>
                                                                            <hr className="dropdown-divider" />
                                                                            <Link
                                                                                className="dropdown-item has-text-danger"
                                                                                onClick={() => handleDeleteClick(expense)}
                                                                            >
                                                                                <span className="icon">
                                                                                    <i className="fas fa-trash-alt"></i>
                                                                                </span>
                                                                                <span>Delete</span>
                                                                            </Link>
                                                                        </>
                                                                    )}
                                                                    {expense.is_deleted && (
                                                                        <>
                                                                            <hr className="dropdown-divider" />
                                                                            <Link
                                                                                className="dropdown-item has-text-success"
                                                                                onClick={() => handleRestoreClick(expense)}
                                                                            >
                                                                                <span className="icon">
                                                                                    <i className="fas fa-undo"></i>
                                                                                </span>
                                                                                <span>Restore</span>
                                                                            </Link>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}
                                                            {!canEdit(userRole) && (
                                                                <Link to={`${expense.id}/details`} className="dropdown-item">
                                                                    <span className="icon">
                                                                        <i className="fas fa-info-circle"></i>
                                                                    </span>
                                                                    <span>View Details</span>
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="11" className="has-text-centered">
                                            No expenses found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr className="has-background-light">
                                    <td colSpan="2" className="has-text-weight-bold">Total</td>
                                    <td className="has-text-weight-bold">
                                        {formatCurrency(totalAmount)}
                                    </td>
                                    <td colSpan="8"></td>
                                </tr>
                            </tfoot>
                        </table>

                        {/* Pagination with Page Size Info */}
                        <nav className="level">
                            <div className="level-left">
                                <div className="level-item">
                                    <span className="tag is-light">
                                        Showing {expenses.length > 0 ? (currentPage - 1) * perPage + 1 : 0} - {Math.min(currentPage * perPage, (currentPage - 1) * perPage + expenses.length)} of {expenses.length} items
                                    </span>
                                </div>
                            </div>
                            <div className="level-right">
                                <div className="level-item">
                                    <nav className="pagination" role="navigation" aria-label="pagination">
                                        <button
                                            className="pagination-previous button is-small"
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                        >
                                            <i className="fa fa-chevron-left"></i>
                                        </button>
                                        <span className="pagination-page" style={{ margin: "0 10px" }}>
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            className="pagination-next button is-small"
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                        >
                                            <i className="fa fa-chevron-right"></i>
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </nav>
                    </>
                )}

                {/* Delete Confirmation Dialog with Reason */}
                {isConfirmDialogOpen && (
                    <div className="modal is-active">
                        <div className="modal-background" onClick={cancelDeleteExpense}></div>
                        <div className="modal-card">
                            <header className="modal-card-head">
                                <p className="modal-card-title">Confirm Delete</p>
                                <button className="delete" aria-label="close" onClick={cancelDeleteExpense}></button>
                            </header>
                            <section className="modal-card-body">
                                <p>Are you sure you want to delete this expense?</p>
                                <div className="box" style={{ marginTop: "1rem" }}>
                                    <p><strong>Name:</strong> {selectedExpense?.name}</p>
                                    <p><strong>Amount:</strong> {formatCurrency(selectedExpense?.amount)}</p>
                                    <p><strong>Date:</strong> {formatToDayMonthYear(selectedExpense?.incurred_on)}</p>
                                    {selectedExpense?.is_route_specific && selectedExpense?.collection_route?.name && (
                                        <p><strong>Route:</strong> {selectedExpense.collection_route.name}</p>
                                    )}
                                </div>
                                
                                <div className="field">
                                    <label className="label">Reason for Deletion <span className="has-text-danger">*</span></label>
                                    <div className="control">
                                        <textarea
                                            className="textarea"
                                            placeholder="Please provide a reason for deleting this expense..."
                                            value={deletionReason}
                                            onChange={(e) => setDeletionReason(e.target.value)}
                                            rows="3"
                                        />
                                    </div>
                                    <p className="help">This reason will be recorded for audit purposes.</p>
                                </div>
                                
                                <p className="has-text-danger">This action can be reversed by restoring the expense.</p>
                            </section>
                            <footer className="modal-card-foot">
                                <button 
                                    className="button is-danger" 
                                    onClick={confirmDeleteExpense}
                                    disabled={!deletionReason.trim()}
                                >
                                    <i className="fa fa-trash"></i>&nbsp; Delete
                                </button>
                                <button className="button" onClick={cancelDeleteExpense}>
                                    Cancel
                                </button>
                            </footer>
                        </div>
                    </div>
                )}

                {/* Restore Confirmation Dialog */}
                {isRestoreDialogOpen && (
                    <div className="modal is-active">
                        <div className="modal-background" onClick={cancelRestoreExpense}></div>
                        <div className="modal-card">
                            <header className="modal-card-head">
                                <p className="modal-card-title">Confirm Restore</p>
                                <button className="delete" aria-label="close" onClick={cancelRestoreExpense}></button>
                            </header>
                            <section className="modal-card-body">
                                <p>Are you sure you want to restore this expense?</p>
                                <div className="box" style={{ marginTop: "1rem" }}>
                                    <p><strong>Name:</strong> {selectedExpense?.name}</p>
                                    <p><strong>Amount:</strong> {formatCurrency(selectedExpense?.amount)}</p>
                                    <p><strong>Date:</strong> {formatToDayMonthYear(selectedExpense?.incurred_on)}</p>
                                    {selectedExpense?.deleted_by?.name && (
                                        <p><strong>Deleted By:</strong> {selectedExpense.deleted_by.name}</p>
                                    )}
                                    {selectedExpense?.deleted_at && (
                                        <p><strong>Deleted At:</strong> {formatTimestamp(selectedExpense.deleted_at)}</p>
                                    )}
                                    {selectedExpense?.deletion_reason && (
                                        <p><strong>Deletion Reason:</strong> {selectedExpense.deletion_reason}</p>
                                    )}
                                </div>
                                <p className="has-text-success">This will make the expense visible and editable again.</p>
                            </section>
                            <footer className="modal-card-foot">
                                <button className="button is-success" onClick={confirmRestoreExpense}>
                                    <i className="fa fa-undo"></i>&nbsp; Restore
                                </button>
                                <button className="button" onClick={cancelRestoreExpense}>
                                    Cancel
                                </button>
                            </footer>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default Expenses;